import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, RotateCw, CalendarClock, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { journalAPI, accountsAPI, organizationAPI } from '../lib/api';
import JournalAssistant from '../components/ai/JournalAssistant';
import { toast } from 'sonner';

const hFont = { fontFamily: 'Manrope, sans-serif' };

function getFiscalYearEnd(fyStartMonth, refDateStr) {
  const ref = new Date(refDateStr + 'T00:00:00');
  const year = ref.getFullYear();
  if (fyStartMonth === 1) return `${year}-12-31`;
  const endMonth = fyStartMonth - 1;
  let endYear = ref.getMonth() + 1 >= fyStartMonth ? year + 1 : year;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  return `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

export default function JournalEntryFormPage() {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [fyStart, setFyStart] = useState(1);
  const [loading, setLoading] = useState(false);

  // Form state
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  let _lineId = 0;
  const _newLine = (overrides = {}) => ({ _uid: `line-${++_lineId}-${Date.now()}`, account_id: '', debit: '', credit: '', description: '', ...overrides });
  const [lines, setLines] = useState([_newLine(), _newLine()]);

  // Recurring
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('monthly');
  const [recurrenceEnd, setRecurrenceEnd] = useState('');

  // Accrual
  const [isAccrual, setIsAccrual] = useState(false);
  const [accrualReverseDate, setAccrualReverseDate] = useState('');

  useEffect(() => {
    Promise.all([
      accountsAPI.getAll().then(r => setAccounts(r.data.items || r.data)),
      organizationAPI.get().then(r => setFyStart(r.data.fiscal_year_start || 1)),
    ]).catch(() => {});
  }, []);

  const fiscalYearEnd = useMemo(() => {
    if (!date) return '';
    return getFiscalYearEnd(fyStart, date);
  }, [date, fyStart]);

  const recurrenceDates = useMemo(() => {
    if (!isRecurring || !frequency || !recurrenceEnd || !date) return [];
    const deltas = { monthly: 1, quarterly: 3, semi_annual: 6 };
    const delta = deltas[frequency];
    if (!delta) return [];
    const start = new Date(date + 'T00:00:00');
    const end = new Date(recurrenceEnd + 'T00:00:00');
    const dates = [];
    let cur = new Date(start);
    cur.setMonth(cur.getMonth() + delta);
    while (cur <= end) {
      dates.push(cur.toISOString().slice(0, 10));
      cur = new Date(cur);
      cur.setMonth(cur.getMonth() + delta);
    }
    return dates;
  }, [isRecurring, frequency, recurrenceEnd, date]);

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const updateLine = (i, field, val) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: val };
    setLines(next);
  };
  const addLine = () => setLines([...lines, _newLine()]);
  const removeLine = (i) => { if (lines.length > 2) setLines(lines.filter((_, j) => j !== i)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error('Description is required');
    if (!isBalanced) return toast.error('Debits must equal credits');
    if (lines.some(l => !l.account_id)) return toast.error('All lines must have an account selected');

    setLoading(true);
    try {
      const payload = {
        date,
        description: description.trim(),
        reference: reference.trim() || null,
        lines: lines.map(l => ({
          account_id: l.account_id,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description || '',
        })),
        is_recurring: isRecurring,
        frequency: isRecurring ? frequency : null,
        recurrence_end_date: isRecurring ? recurrenceEnd : null,
        is_accrual: isAccrual,
        accrual_reverse_date: isAccrual ? accrualReverseDate : null,
      };
      await journalAPI.create(payload);
      const count = isRecurring ? recurrenceDates.length + 1 : 1;
      toast.success(`${count} journal ${count > 1 ? 'entries' : 'entry'} created${isAccrual ? ' (+ reversal)' : ''}`);
      navigate('/journal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create journal entry');
    }
    setLoading(false);
  };

  const sortedAccounts = useMemo(() =>
    [...accounts].sort((a, b) => (a.code || '').localeCompare(b.code || '')),
  [accounts]);

  return (
    <TooltipProvider>
    <div className="max-w-4xl mx-auto py-6 px-4 md:px-6" data-testid="journal-entry-form">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/journal')} data-testid="journal-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-bold text-stone-900" style={hFont}>New Journal Entry</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold text-stone-600 mb-1.5">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="je-date" required />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold text-stone-600 mb-1.5">Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Monthly rent payment" data-testid="je-description" required />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-stone-600 mb-1.5">Reference (optional)</Label>
          <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g. INV-2024-001" data-testid="je-reference" className="max-w-xs" />
        </div>

        {/* AI Journal Assistant */}
        <JournalAssistant
          description={description}
          existingLines={lines}
          onApplyEntry={(suggestedLines) => {
            // Map AI-suggested lines to account_ids where possible
            const mapped = suggestedLines.map(sl => {
              const match = accounts.find(a =>
                a.name.toLowerCase().includes((sl.account_name || '').toLowerCase())
              );
              return {
                _uid: `ai-${++_lineId}-${Date.now()}`,
                account_id: match?.id || '',
                debit: sl.debit ? String(sl.debit) : '',
                credit: sl.credit ? String(sl.credit) : '',
                description: '',
              };
            });
            if (mapped.length >= 2) setLines(mapped);
          }}
          onSetRecurring={() => setIsRecurring(true)}
        />

        {/* Journal lines */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-bold text-stone-900" style={hFont}>Journal Lines</Label>
            <Button type="button" variant="outline" size="sm" onClick={addLine} className="h-8 text-xs" data-testid="je-add-line">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add line
            </Button>
          </div>

          <div className="border border-stone-200 rounded-xl overflow-hidden">
            {/* Desktop table */}
            <table className="w-full text-sm hidden md:table">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left text-xs font-semibold text-stone-500 px-3 py-2.5 w-[38%]">Account</th>
                  <th className="text-left text-xs font-semibold text-stone-500 px-3 py-2.5 w-[18%]">Debit</th>
                  <th className="text-left text-xs font-semibold text-stone-500 px-3 py-2.5 w-[18%]">Credit</th>
                  <th className="text-left text-xs font-semibold text-stone-500 px-3 py-2.5 w-[22%]">Line note</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={line._uid} className="border-b border-stone-100 last:border-0" data-testid={`je-line-${i}`}>
                    <td className="px-2 py-2">
                      <select
                        value={line.account_id}
                        onChange={e => updateLine(i, 'account_id', e.target.value)}
                        className="w-full h-9 rounded-md border border-stone-200 text-sm px-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#D94E18]/20"
                        data-testid={`je-line-account-${i}`}
                        required
                      >
                        <option value="">Select account</option>
                        {sortedAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        type="number" step="0.01" min="0"
                        value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)}
                        placeholder="0.00" className="h-9 text-sm"
                        data-testid={`je-line-debit-${i}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        type="number" step="0.01" min="0"
                        value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)}
                        placeholder="0.00" className="h-9 text-sm"
                        data-testid={`je-line-credit-${i}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={line.description} onChange={e => updateLine(i, 'description', e.target.value)}
                        placeholder="Optional" className="h-9 text-sm"
                        data-testid={`je-line-note-${i}`}
                      />
                    </td>
                    <td className="px-1 py-2 text-center">
                      {lines.length > 2 && (
                        <button type="button" onClick={() => removeLine(i)} className="p-1.5 text-stone-400 hover:text-red-500 transition-colors" data-testid={`je-remove-line-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-stone-50 border-t border-stone-200">
                  <td className="px-3 py-2.5 text-xs font-bold text-stone-700 text-right">Totals</td>
                  <td className="px-3 py-2.5 text-sm font-bold text-stone-900" data-testid="je-total-debit">{totalDebit.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-sm font-bold text-stone-900" data-testid="je-total-credit">{totalCredit.toFixed(2)}</td>
                  <td className="px-3 py-2.5">
                    {!isBalanced && totalDebit + totalCredit > 0 && (
                      <span className="text-xs font-medium text-red-500" data-testid="je-imbalance">
                        Difference: {Math.abs(totalDebit - totalCredit).toFixed(2)}
                      </span>
                    )}
                    {isBalanced && totalDebit > 0 && (
                      <span className="text-xs font-medium text-emerald-600">Balanced</span>
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            {/* Mobile card layout */}
            <div className="md:hidden divide-y divide-stone-100">
              {lines.map((line, i) => (
                <div key={line._uid} className="p-3 space-y-2" data-testid={`je-line-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-stone-400 uppercase">Line {i + 1}</span>
                    {lines.length > 2 && (
                      <button type="button" onClick={() => removeLine(i)} className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center" data-testid={`je-remove-line-mobile-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    )}
                  </div>
                  <select
                    value={line.account_id}
                    onChange={e => updateLine(i, 'account_id', e.target.value)}
                    className="w-full h-10 rounded-lg border border-stone-200 text-sm px-3 bg-white"
                    data-testid={`je-line-account-${i}`}
                    required
                  >
                    <option value="">Select account</option>
                    {sortedAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-stone-400 mb-0.5 block">Debit</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)}
                        placeholder="0.00" className="h-10 text-sm"
                        data-testid={`je-line-debit-${i}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-stone-400 mb-0.5 block">Credit</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)}
                        placeholder="0.00" className="h-10 text-sm"
                        data-testid={`je-line-credit-${i}`}
                      />
                    </div>
                  </div>
                  <Input
                    value={line.description} onChange={e => updateLine(i, 'description', e.target.value)}
                    placeholder="Line note (optional)" className="h-10 text-sm"
                    data-testid={`je-line-note-${i}`}
                  />
                </div>
              ))}
              {/* Mobile totals */}
              <div className="p-3 bg-stone-50 flex items-center justify-between">
                <span className="text-xs font-bold text-stone-700">Totals</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs"><span className="text-stone-400">Dr</span> <span className="font-bold text-stone-900" data-testid="je-total-debit-mobile">{totalDebit.toFixed(2)}</span></span>
                  <span className="text-xs"><span className="text-stone-400">Cr</span> <span className="font-bold text-stone-900" data-testid="je-total-credit-mobile">{totalCredit.toFixed(2)}</span></span>
                  {isBalanced && totalDebit > 0 ? (
                    <span className="text-[10px] font-medium text-emerald-600">Balanced</span>
                  ) : totalDebit + totalCredit > 0 ? (
                    <span className="text-[10px] font-medium text-red-500">Off by {Math.abs(totalDebit - totalCredit).toFixed(2)}</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recurring toggle */}
        <div className="border border-stone-200 rounded-xl p-5 space-y-4" data-testid="je-recurring-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <RotateCw className="w-4 h-4 text-[#D94E18]" />
              <span className="text-sm font-bold text-stone-900" style={hFont}>Recurring Entry</span>
              <Tooltip>
                <TooltipTrigger><Info className="w-3.5 h-3.5 text-stone-400" /></TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Creates multiple entries at regular intervals. Cannot exceed the fiscal year boundary ({fiscalYearEnd}).
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch checked={isRecurring} onCheckedChange={setIsRecurring} data-testid="je-recurring-toggle" />
          </div>

          {isRecurring && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1.5">Frequency</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="je-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1.5">Until (max: {fiscalYearEnd})</Label>
                <Input
                  type="date" value={recurrenceEnd}
                  onChange={e => setRecurrenceEnd(e.target.value)}
                  max={fiscalYearEnd}
                  data-testid="je-recurrence-end"
                  required={isRecurring}
                />
              </div>
              {recurrenceDates.length > 0 && (
                <div className="md:col-span-2 bg-stone-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-stone-600 mb-1.5">
                    Preview: {recurrenceDates.length + 1} entries will be created
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[11px] bg-[#D94E18]/10 text-[#D94E18] font-medium px-2 py-0.5 rounded">{date} (primary)</span>
                    {recurrenceDates.map(d => (
                      <span key={d} className="text-[11px] bg-stone-200 text-stone-700 px-2 py-0.5 rounded">{d}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Accrual toggle */}
        <div className="border border-stone-200 rounded-xl p-5 space-y-4" data-testid="je-accrual-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <CalendarClock className="w-4 h-4 text-[#D94E18]" />
              <span className="text-sm font-bold text-stone-900" style={hFont}>Accrual / Prepayment</span>
              <Tooltip>
                <TooltipTrigger><Info className="w-3.5 h-3.5 text-stone-400" /></TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  Automatically creates a reversing entry on the specified date. Useful for month-end accruals and prepayments.
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch checked={isAccrual} onCheckedChange={setIsAccrual} data-testid="je-accrual-toggle" />
          </div>

          {isAccrual && (
            <div className="max-w-xs pt-1">
              <Label className="text-xs font-semibold text-stone-600 mb-1.5">Reversal Date</Label>
              <Input
                type="date" value={accrualReverseDate}
                onChange={e => setAccrualReverseDate(e.target.value)}
                min={date}
                data-testid="je-accrual-date"
                required={isAccrual}
              />
              {accrualReverseDate && (
                <p className="text-[11px] text-stone-500 mt-1.5">
                  A reversing entry (debits/credits swapped) will be created on {accrualReverseDate}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            type="submit" disabled={loading || !isBalanced || totalDebit === 0}
            className="bg-[#D94E18] hover:bg-[#C2410C] text-white h-10 px-6 text-sm font-semibold rounded-full"
            data-testid="je-submit-btn"
          >
            {loading ? 'Creating...' : isRecurring ? `Create ${recurrenceDates.length + 1} entries` : 'Create entry'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/journal')} className="h-10 px-6 text-sm rounded-full">
            Cancel
          </Button>
        </div>
      </form>
    </div>
    </TooltipProvider>
  );
}
